/**
 * Chart event handlers - click, resize, and chart refresh logic
 */

import { skipNextPanelUpdate, showMyRolesActive, setSkipNextPanelUpdate } from '../state.js';
import { displayNodeData, updateSelectedNodeTitle } from '../panel.js';
import type { CirclePackChart } from '../../shared/types.js';

/**
 * Create click handler with delayed single-click detection
 */
export function createClickHandler(
    chart: CirclePackChart,
    clickTimerRef: { current: number | null }
): (node: any) => void {
    return (node) => {
        // Delay single-click to avoid firing during double-click
        if (clickTimerRef.current) clearTimeout(clickTimerRef.current);
        
        clickTimerRef.current = window.setTimeout(() => {
            if (skipNextPanelUpdate) {
                setSkipNextPanelUpdate(false);
                if (node) {
                    chart.zoomToNode(node);
                }
                clickTimerRef.current = null;
                return;
            }

            // If "show my roles" is active, don't update the panel - just zoom
            if (showMyRolesActive) {
                if (node) {
                    chart.zoomToNode(node);
                }
                clickTimerRef.current = null;
                return;
            }

            displayNodeData(node);
            updateSelectedNodeTitle(node);
            if (node) {
                chart.zoomToNode(node);
            }
            clickTimerRef.current = null;
        }, 250);
    };
}

/**
 * Create resize handler for chart responsiveness
 */
export function createResizeHandler(chart: CirclePackChart): () => void {
    let resizeTimer: number | null = null;
    const defaultDuration = 800; // CirclePack default transition duration
    
    return () => {
        // Disable transition for window resize
        (chart as any).transitionDuration?.(0);

        const newChartWidth = window.innerWidth * 0.7;
        chart.width(newChartWidth).height(window.innerHeight);

        // Debounce: only restore after resize events stop coming
        if (resizeTimer) {
            clearTimeout(resizeTimer);
        }
        
        resizeTimer = window.setTimeout(() => {
            (chart as any).transitionDuration?.(defaultDuration);
            resizeTimer = null;
        }, 100);
    };
}
